import React from 'react';

/**
 * AdminBetLog — Live feed of all bets and recent settlements.
 * Two sections: active (locked) bets and settled ledger entries.
 */
const AdminBetLog = ({ bets, ledger }) => {
    // Active bets sorted by most recent
    const activeBets = Object.entries(bets)
        .filter(([, b]) => b.locked && !b.settled)
        .sort(([a], [b]) => {
            const tsA = parseInt(a.split('_slot_')[1]) || 0;
            const tsB = parseInt(b.split('_slot_')[1]) || 0;
            return tsB - tsA;
        })
        .slice(0, 30);

    // Recent settlements (last 30)
    const recentSettlements = [...ledger].reverse().slice(0, 30);

    return (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
            {/* Active Bets */}
            <div className="border border-white/5 bg-white/[0.02] rounded-lg overflow-hidden">
                <div className="px-5 py-3 border-b border-white/5 flex justify-between items-center">
                    <h3 className="text-sm uppercase tracking-[0.25em] text-gray-400 font-black">
                        Active Bets
                    </h3>
                    <span className="text-sm font-mono text-emerald-400">
                        {activeBets.length} open
                    </span>
                </div>
                <div className="max-h-[400px] overflow-y-auto custom-scrollbar">
                    <table className="w-full text-sm">
                        <thead className="sticky top-0 bg-[#0a0a14]">
                            <tr className="text-xs uppercase tracking-[0.2em] text-gray-500 border-b border-white/5">
                                <th className="px-4 py-2 text-left font-bold">Slot</th>
                                <th className="px-3 py-2 text-left font-bold">Dir</th>
                                <th className="px-3 py-2 text-right font-bold">Amount</th>
                            </tr>
                        </thead>
                        <tbody>
                            {activeBets.map(([key, bet]) => {
                                const [coinId] = key.split('_slot_');
                                const ts = parseInt(key.split('_slot_')[1]);
                                const d = ts ? new Date(ts) : null;
                                const time = d ? `${d.getHours() % 12 || 12}:${String(d.getMinutes()).padStart(2, '0')}` : '—';
                                return (
                                    <tr key={key} className="border-b border-white/[0.03] hover:bg-white/[0.02] transition-colors">
                                        <td className="px-4 py-2 font-mono text-gray-300">
                                            <span className="text-white font-bold">{coinId?.toUpperCase()?.slice(0, 4)}</span>
                                            <span className="text-gray-600 ml-1">@ {time}</span>
                                        </td>
                                        <td className={`px-3 py-2 font-mono font-bold ${bet.direction === 'UP' ? 'text-green-400' : 'text-red-400'}`}>
                                            {bet.direction}
                                        </td>
                                        <td className="px-3 py-2 text-right font-mono text-gray-300">
                                            ${Number(bet.amount).toLocaleString()}
                                        </td>
                                    </tr>
                                );
                            })}
                            {activeBets.length === 0 && (
                                <tr><td colSpan="3" className="px-4 py-6 text-center text-gray-600 text-sm">No active bets</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Recent Settlements */}
            <div className="border border-white/5 bg-white/[0.02] rounded-lg overflow-hidden">
                <div className="px-5 py-3 border-b border-white/5 flex justify-between items-center">
                    <h3 className="text-sm uppercase tracking-[0.25em] text-gray-400 font-black">
                        Recent Settlements
                    </h3>
                    <span className="text-sm font-mono text-purple-400">
                        {ledger.length} total
                    </span>
                </div>
                <div className="max-h-[400px] overflow-y-auto custom-scrollbar">
                    <table className="w-full text-sm">
                        <thead className="sticky top-0 bg-[#0a0a14]">
                            <tr className="text-xs uppercase tracking-[0.2em] text-gray-500 border-b border-white/5">
                                <th className="px-4 py-2 text-left font-bold">Coin</th>
                                <th className="px-3 py-2 text-left font-bold">Dir</th>
                                <th className="px-3 py-2 text-right font-bold">Amount</th>
                                <th className="px-3 py-2 text-right font-bold">Net</th>
                                <th className="px-3 py-2 text-right font-bold">Fee</th>
                                <th className="px-4 py-2 text-center font-bold">Result</th>
                            </tr>
                        </thead>
                        <tbody>
                            {recentSettlements.map((entry, i) => (
                                <tr key={i} className="border-b border-white/[0.03] hover:bg-white/[0.02] transition-colors">
                                    <td className="px-4 py-2 font-mono font-bold text-white">{entry.coin}</td>
                                    <td className={`px-3 py-2 font-mono font-bold ${entry.direction === 'UP' ? 'text-green-400' : 'text-red-400'}`}>
                                        {entry.direction}
                                    </td>
                                    <td className="px-3 py-2 text-right font-mono text-gray-300">
                                        ${entry.amount?.toLocaleString()}
                                    </td>
                                    <td className={`px-3 py-2 text-right font-mono font-bold ${entry.net >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                                        {entry.net >= 0 ? '+' : ''}{entry.net?.toFixed(2)}
                                    </td>
                                    <td className="px-3 py-2 text-right font-mono text-amber-400">
                                        ${entry.fee?.toFixed(2)}
                                    </td>
                                    <td className="px-4 py-2 text-center">
                                        <span className={`text-xs font-black uppercase tracking-wider px-2 py-0.5 rounded ${entry.result === 'WIN' ? 'bg-green-500/10 text-green-400' :
                                                entry.result === 'LOSS' ? 'bg-red-500/10 text-red-400' :
                                                    'bg-gray-500/10 text-gray-400'
                                            }`}>
                                            {entry.result}
                                        </span>
                                    </td>
                                </tr>
                            ))}
                            {recentSettlements.length === 0 && (
                                <tr><td colSpan="6" className="px-4 py-6 text-center text-gray-600 text-sm">No settlements yet</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default AdminBetLog;
